import { useState } from "react";
import carouselImage from "../assets/images/trading/buy_sell_carousel_item.svg";
import { Button, Typography } from "./ui";
import { PlusIcon } from "lucide-react";
import { useFeatureFlagsByCategory } from "../hooks/query/queries";
import { SecuredFooter } from "./ui/secured-footer";

interface BuySellCarouselProps {
  onGetStarted: () => void;
  fromDashboard?: boolean;
}

const BuySellCarousel = ({
  onGetStarted,
  fromDashboard = false,
}: BuySellCarouselProps) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const slides = [
    {
      id: 0,
      title: "Get In When It Feels Right",
      description:
        "No rush, no panic. Just an easy way to own a piece of the action when you're ready.",
      image: carouselImage,
    },
    {
      id: 1,
      title: "Cash Out, Calmly. No Chaos.",
      description:
        "When it's time to take money off the table, it's quick, simple, and fuss-free.",
      image: carouselImage,
    },
    {
      id: 2,
      title: "Know What You're Paying For",
      description:
        "Clear, upfront pricing so you never have to second-guess your decisions.",
      image: carouselImage,
    },
  ];

  const handleNext = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlide = slides.at(activeSlide);
  const isLastSlide = activeSlide === slides.length - 1;

  const renderOutlineButton = (text: string, onClick: () => void) => (
    <Button
      onClick={onClick}
      variant="ghost"
      size="lg"
      className="text-xs md:text-sm border border-bg-section p-4 md:p-6 min-w-auto md:min-w-[140px] rounded-full text-text-primary"
    >
      {text}
    </Button>
  );

  const renderPrimaryButton = (
    text: string,
    onClick: () => void,
    customClass?: string
  ) => (
    <Button
      className={`btn-primary text-xs md:text-sm !w-fit p-4 md:p-6 ${customClass}`}
      onClick={onClick}
    >
      <PlusIcon className="w-3 h-3 md:w-4 md:h-4" />
      <Typography variant="p" className="text-white text-xs md:text-sm">
        {text}
      </Typography>
    </Button>
  );

  const getLeftButton = () => {
    if (activeSlide > 0) {
      return renderOutlineButton("Previous", handlePrevious);
    }
    if (featureFlags?.deposit) {
      return renderOutlineButton("Deposit Cash", onGetStarted);
    }
    return null;
  };

  const getRightButton = () => {
    if (isLastSlide) {
      return renderPrimaryButton(
        "Get Started",
        onGetStarted,
        "min-w-auto md:min-w-[140px]"
      );
    }
    return renderPrimaryButton("Next", handleNext);
  };

  return (
    <div className="bg-white rounded-lg">
      <div
        className={`bg-white rounded-lg px-6 py-24 flex flex-col justify-center ${fromDashboard ? "" : "h-[650px]"}`}
      >
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          <div className="flex items-center justify-center mb-2">
            <img
              src={currentSlide.image}
              alt={currentSlide.title}
              className="w-28 lg:w-[160px]"
            />
          </div>

          <Typography
            variant="h3"
            className="text-lg lg:text-2xl font-semibold text-brand-primary"
          >
            {currentSlide.title}
          </Typography>

          <Typography
            variant="p"
            className="text-sm text-gray-600 leading-relaxed max-w-sm"
          >
            {currentSlide.description}
          </Typography>
        </div>

        <div className="flex justify-center gap-4 mt-4 mb-10">
          {getLeftButton()}
          {getRightButton()}
        </div>

        <div className="flex justify-center space-x-2 mt-6 mb-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index === activeSlide ? "bg-black w-6" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
      {!fromDashboard && <SecuredFooter className="mt-0" />}
    </div>
  );
};

export default BuySellCarousel;
